import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { iconoirAntenna, iconoirBuilding, iconoirMapPin, iconoirSmartphoneDevice } from '@ng-icons/iconoir';
import { TranslocoModule } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, Subject, switchMap } from 'rxjs';
import { COMMAND_PALETTE_CONFIG, CommandResultKind } from './command-palette.config';
import { CommandResult, CommandSearchService } from './command-search.service';
import { AdminScopeService } from '../services/admin-scope.service';

interface PaletteRow extends CommandResult {
  index: number;
  /** First row of its kind — drives the group header. */
  groupStart: boolean;
}

/**
 * Global command palette — opens on ⌘K (macOS) / Alt+K (elsewhere) from any page, searches
 * sites / devices / gateways / organizations (org-scoped, see {@link CommandSearchService}) and
 * navigates to the picked result. Keyboard-first (↑/↓/Enter/Esc); mount once per app shell.
 *
 * Shared by both apps: `/sites/:id` and `/gateways/:id` exist in each; the device detail route
 * differs (inside `/devices/trackers/:id`, integrator `/devices/:id`) and comes from the config.
 * The org scope it searches within comes from the shared {@link AdminScopeService}.
 * Chaque app fournit `COMMAND_PALETTE_CONFIG` : types ouvrables selon ses gardes, source des
 * appareils (intégrateur : trackers seulement).
 */
@Component({
  selector: 'wh-command-palette',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslocoModule, NgIconComponent],
  providers: [provideIcons({ iconoirMapPin, iconoirSmartphoneDevice, iconoirAntenna, iconoirBuilding })],
  template: `
    @if (visible()) {
      <div class="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-3 pt-[12vh]" (click)="close()">
        <div
          class="flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-800"
          (click)="$event.stopPropagation()">
          <!-- Search input -->
          <div class="flex items-center gap-2 border-b border-gray-100 px-4 dark:border-slate-700">
            <span class="text-gray-400">⌕</span>
            <input
              #searchInput
              type="text"
              class="h-12 min-w-0 flex-1 border-0 bg-transparent text-base text-gray-900 outline-none focus:ring-0 dark:text-slate-100"
              [placeholder]="'commandPalette.placeholder' | transloco"
              [ngModel]="query()"
              (ngModelChange)="onQueryChange($event)" />
            <kbd
              class="hidden rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-400 sm:inline dark:border-slate-600">
              ESC
            </kbd>
          </div>

          <!-- Results -->
          <div class="flex-1 overflow-y-auto py-1">
            @for (row of rows(); track row.kind + row.id) {
              @if (row.groupStart) {
                <div
                  class="px-4 pb-1 pt-2 text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  {{ 'commandPalette.groups.' + row.kind | transloco }}
                </div>
              }
              <button
                type="button"
                class="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors"
                [class.bg-eletricPurple-50]="row.index === highlighted()"
                [class.dark:bg-slate-700]="row.index === highlighted()"
                (mouseenter)="highlighted.set(row.index)"
                (click)="select(row)">
                <ng-icon [name]="icon(row.kind)" size="1.15rem" class="shrink-0 text-gray-400 dark:text-slate-500" />
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-base text-gray-900 dark:text-slate-100">{{ row.label }}</span>
                  @if (row.sublabel) {
                    <span class="block truncate text-sm text-gray-400 dark:text-slate-500">{{ row.sublabel }}</span>
                  }
                </span>
              </button>
            } @empty {
              <p class="px-4 py-6 text-center text-sm text-gray-400 dark:text-slate-500">
                @if (query().trim().length < 2) {
                  {{ 'commandPalette.hint' | transloco }}
                } @else if (loading()) {
                  {{ 'commandPalette.searching' | transloco }}
                } @else {
                  {{ 'commandPalette.empty' | transloco }}
                }
              </p>
            }
          </div>

          <!-- Footer hint -->
          <div
            class="border-t border-gray-100 px-4 py-2 text-xs text-gray-400 dark:border-slate-700 dark:text-slate-500">
            {{ 'commandPalette.footer' | transloco }}
          </div>
        </div>
      </div>
    }
  `,
})
export class CommandPaletteComponent {
  protected readonly visible = signal(false);
  protected readonly query = signal('');
  protected readonly results = signal<CommandResult[]>([]);
  protected readonly loading = signal(false);
  protected readonly highlighted = signal(0);

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  readonly #search$ = new Subject<string>();

  #search = inject(CommandSearchService);
  #router = inject(Router);
  #adminScope = inject(AdminScopeService);
  #config = inject(COMMAND_PALETTE_CONFIG);
  #destroyRef = inject(DestroyRef);

  /** Open shortcut differs per OS: ⌘K on macOS, Alt+K elsewhere (Ctrl+K is taken by the browser
   * address bar on Windows/Linux). */
  readonly #isMac = /Mac|iPhone|iPad|iPod/i.test(
    (typeof navigator !== 'undefined' && (navigator.platform || navigator.userAgent)) || ''
  );

  /** Human-readable open shortcut for the navbar hint — matches the handler, per OS. */
  readonly shortcutLabel = this.#isMac ? '⌘ + K' : 'Alt + K';

  /** Flatten results into rows with a group-start flag for the headers. */
  protected readonly rows = computed<PaletteRow[]>(() => {
    const list = this.results();
    return list.map((r, i) => ({ ...r, index: i, groupStart: i === 0 || list[i - 1].kind !== r.kind }));
  });

  constructor() {
    this.#search$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap(q => this.#search.search(q)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(results => {
        this.results.set(results);
        this.highlighted.set(0);
        this.loading.set(false);
      });
  }

  /** ⌘K (macOS) / Alt+K (Windows/Linux) toggles the palette from anywhere; the rest only when open.
   * Uses {@code e.code} so it's keyboard-layout independent (Alt can remap the produced character). */
  @HostListener('document:keydown', ['$event'])
  protected onKeydown(e: KeyboardEvent): void {
    const modifier = this.#isMac ? e.metaKey : e.altKey;
    if (modifier && (e.code === 'KeyK' || e.key.toLowerCase() === 'k')) {
      e.preventDefault();
      this.visible() ? this.close() : this.open();
      return;
    }
    if (!this.visible()) {
      return;
    }
    const max = this.results().length - 1;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.highlighted.set(Math.min(max, this.highlighted() + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.highlighted.set(Math.max(0, this.highlighted() - 1));
    } else if (e.key === 'Enter') {
      const row = this.results()[this.highlighted()];
      if (row) {
        e.preventDefault();
        this.select(row);
      }
    }
  }

  /** Public so the navbar trigger can open it via a template ref. */
  open(): void {
    this.query.set('');
    this.results.set([]);
    this.highlighted.set(0);
    this.visible.set(true);
    setTimeout(() => this.searchInput()?.nativeElement.focus(), 0);
  }

  protected close(): void {
    this.visible.set(false);
  }

  protected onQueryChange(q: string): void {
    this.query.set(q);
    this.loading.set(q.trim().length >= 2);
    this.#search$.next(q);
  }

  protected select(row: CommandResult): void {
    this.close();
    switch (row.kind) {
      case 'site':
        void this.#router.navigate(['/sites', row.id]);
        break;
      case 'device':
        void this.#router.navigate(this.#config.deviceRoute?.(row.id) ?? ['/devices', row.id]);
        break;
      case 'gateway':
        void this.#router.navigate(['/gateways', row.id]);
        break;
      case 'org':
        // Apply the org scope, then land on the sites list of that org.
        this.#adminScope.setOrg(row.id);
        void this.#router.navigate(['/sites']);
        break;
    }
  }

  /** Iconoir over emoji: emoji render per-OS (and in colour), which reads as a toy next to the
   * rest of the chrome. These follow the domain's own vocabulary — a gateway *is* an antenna. */
  protected icon(kind: CommandResultKind): string {
    switch (kind) {
      case 'site':
        return 'iconoirMapPin';
      case 'device':
        return 'iconoirSmartphoneDevice';
      case 'gateway':
        return 'iconoirAntenna';
      case 'org':
        return 'iconoirBuilding';
    }
  }
}
